import './util/polyfills'
import app from './view/app'
import {onContentLoaded} from './util/dom'
import {getScriptTag, parseURL} from './util/url'
import {evolve} from './util/object'
import IAPI from './api/api-interface'
import API, {Callback , Command, Parameter, Queue} from './api/api'
import BackendAPI from './api/backend'
import IFrontendAPI from './api/frontend-interface'
import FrontendAPI from './api/frontend'
import FrontendModAPI from './api/frontend-mod'
import {fetchVendors, fetchDirectVendorList, fetchNecessaryVendorList, VendorList} from './service/vendor-list'
import {PublisherPurposesList} from './service/publisher-purposes-list'
import debug, {init as initDebug, ERROR, WARN} from './util/debug'
import {searchParams} from './util/location'
import getI18n from './i18n'
import {Banner, defaultBanner, videoBanner} from './view/banner'
import {translate} from './util/view'
import {canUseCookies} from './util/storage'

interface Options {
  api: string,
  baseURL: string,
  logLevel?: string,
  render?: boolean,
  renderIn?: string,
  renderVideo?: boolean
}

const parseBoolAttribute = (x: string): boolean => ['', 'true'].includes(x)

const evolveOptions = evolve({
  render: parseBoolAttribute,
  renderVideo: parseBoolAttribute
})

const getOptions = (doc: Document = document): Options => {
  const cmpScript = getScriptTag(doc)
  const baseURL = cmpScript ? parseURL(cmpScript.src).href.replace(/\/iife\/mol-ads-cmp.*\.js$/, '') : ''
  const options: {[key: string]: any} = {
    api: 'frontend',
    baseURL,
    logLevel: searchParams['mol.ads.cmp.log.level'] || 'off',
    render: false
  }

  for (const option in cmpScript ? cmpScript.dataset : {}) {
    if (cmpScript!.dataset.hasOwnProperty(option)) {
      Object.assign(options, evolveOptions({[option]: cmpScript!.dataset[option]}))
    }
  }

  return options as Options
}

const buildFrontendAPI = async (
  baseURL: string,
  renderSettings: boolean,
  banner?: Banner,
  renderInSelector?: string
): Promise<IFrontendAPI> => {
  let api: IFrontendAPI = await FrontendAPI.load(baseURL)

  if (await api.callP('hasStorageAccess')) {
    window.addEventListener('message', FrontendAPI.iframeHandler)
  } else {
    debug('Cannot use iframed API', WARN)
    api.unload()
    api = await FrontendModAPI.load(baseURL) as FrontendModAPI
    window.addEventListener('message', FrontendModAPI.iframeHandler)
  }

  const [
    hasChoiceBeenMade,
    publisherPurposesList,
    i18n
  ] = await Promise.all([
    api.callP('hasChoiceBeenMade'),
    api.callP('getPublisherPurposesList'),
    getI18n(baseURL)
  ])

  const view = app({
    api: api as FrontendAPI,
    banner,
    baseURL,
    hasChoiceBeenMade: hasChoiceBeenMade as boolean,
    publisherPurposesList: publisherPurposesList as PublisherPurposesList,
    renderSettings,
    t: translate(i18n),
    translate: i18n.t.bind(i18n)
  })

  api.view = view

  onContentLoaded(() => {
    let renderIn = renderInSelector && document.querySelector(renderInSelector)

    if (!renderIn) {
      renderIn = document.body
    }

    renderIn.appendChild(view.raw)

    // Temporary workaround to prevent click working on tooltips on desktop devices
    try {
      if (window && window.PageCriteria && !window.PageCriteria.isMobile && !window.PageCriteria.isTablet) {
        for (const tooltipTrigger of renderIn.querySelectorAll('.mol-ads-cmp--tooltip-trigger')) {
          tooltipTrigger.addEventListener('click', (event) => {
            event.preventDefault()
          })
        }
      }
    } catch (error) {
      // Do not break on failure
    }
  })

  return api
}

const buildBackendAPI = (baseURL: string): Promise<BackendAPI> => {
  window.addEventListener('message', BackendAPI.iframeHandler)
  return BackendAPI.load(baseURL)
}

(async () => {
  const options = getOptions()

  if (options.logLevel !== 'off') {
    initDebug({ level: options.logLevel, on: true })
  }

  let api: IAPI
  let banner: Banner | undefined

  if (options.render) {
    banner = defaultBanner
  } else if (options.renderVideo) {
    banner = videoBanner
  }

  try {
    api = options.api === 'frontend'
      ? await buildFrontendAPI(options.baseURL, !options.renderVideo, banner, options.renderIn)
      : await buildBackendAPI(options.baseURL)
    api.callQueue()
  } catch (error) {
    debug(error, ERROR)
    return
  }
})()
